import { useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  notFound,
  useNavigate,
} from "@tanstack/react-router";
import { useActionState, useState } from "react";

import { ImagesEditor } from "../features/images/components/ImagesEditor";
import { LinksEditor } from "../features/links/components/LinksEditor";
import {
  useCancelEventSeries,
  useUpdateEventSeries,
} from "../features/series/api/mutations";
import { seriesQueries } from "../features/series/api/queries";
import { RepeatFields } from "../features/series/components/RepeatFields";
import { readRepeat, toDateInputValue } from "../features/series/form";
import { describeRepeat, parseRepeat } from "../features/series/rrule";
import { AppHeader } from "../shared/ui/AppHeader";

export const Route = createFileRoute("/_authenticated/series/$seriesId/edit")({
  loader: async ({ params, context: { queryClient } }) => {
    const data = await queryClient.ensureQueryData(
      seriesQueries.detail(params.seriesId),
    );
    // Fail in the loader, so the component never renders a missing series.
    if (!data.eventSeries) throw notFound();
  },
  component: EditSeriesPage,
  notFoundComponent: () => (
    <>
      <AppHeader />
      <main className="mx-auto max-w-2xl p-6">
        <h1 className="text-xl font-semibold">Series not found</h1>
        <p className="mt-2 text-sm text-content-muted">
          It may have been cancelled.
        </p>
        <Link
          to="/events"
          className="mt-4 inline-block text-sm text-brand underline"
        >
          Back to events
        </Link>
      </main>
    </>
  ),
});

const inputClass =
  "rounded-md border border-border-subtle bg-surface px-3 py-2 " +
  "focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none";

interface SeriesFormState {
  title: string;
  userErrors: { field: string[]; message: string; code: string }[];
}

/**
 * The series itself — the template every occurrence is generated from.
 *
 * **Why this page exists at all.** Images and links can be attached to a series,
 * and every occurrence without its own inherits them. Until this page there was
 * no way to reach those: `attachSeriesImage` and `addSeriesLink` were API-only,
 * so a poster for a 52-week run had to be attached to all 52 nights one at a
 * time, or through curl.
 *
 * **What it deliberately does not edit.** Venue, price, tags and organisers are
 * all editable through `updateEventSeries`, and are all reachable from any
 * occurrence's edit form via "this and future". Repeating them here would give
 * two routes to the same write with no way to tell which one someone used. What
 * is *only* here is the title, the rule, and the attachments — the things that
 * are properties of the run rather than of a night.
 *
 * Every save is `FUTURE` scope. There is no scope question, because the answer
 * cannot be anything else: `THIS_OCCURRENCE` has no meaning when the subject is
 * the series, and the server rejects it for this mutation.
 */
function EditSeriesPage() {
  const { seriesId } = Route.useParams();
  const navigate = useNavigate();
  const { data } = useSuspenseQuery(seriesQueries.detail(seriesId));
  const updateSeries = useUpdateEventSeries();
  const cancelSeries = useCancelEventSeries();
  const [confirming, setConfirming] = useState(false);

  const series = data.eventSeries;

  const [state, submit, isPending] = useActionState<SeriesFormState, FormData>(
    async (previous, formData) => {
      if (!series) return previous;

      // Narrowed rather than stringified: `FormData.get` can return a File, and
      // String() on one silently yields "[object Object]" — a title that passes
      // the empty check and saves as garbage.
      const raw = formData.get("title");
      const title = typeof raw === "string" ? raw.trim() : "";
      if (!title) {
        return {
          title,
          userErrors: [
            {
              field: ["title"],
              message: "A title is required.",
              code: "INVALID",
            },
          ],
        };
      }

      /*
       * Anchored to the series' own first occurrence, not to today: a weekly
       * rule takes its weekday from the start date, so anchoring to now would
       * quietly move a Thursday run to whatever day it happened to be saved on.
       */
      const repeat = readRepeat(formData, new Date(series.startsAtFirst));

      const { updateEventSeries: result } = await updateSeries.mutateAsync({
        id: series.id,
        // The only scope this page can mean — see the note above.
        scope: "FUTURE",
        input: {
          title,
          // Omitting the rule leaves the stored one alone rather than clearing
          // it, which matters for a CUSTOM rule the form cannot rebuild.
          ...(repeat.rrule ? { rrule: repeat.rrule } : {}),
          until: repeat.until,
        },
      });

      if (result.userErrors.length > 0) {
        return { title, userErrors: result.userErrors };
      }

      return { title, userErrors: [] };
    },
    { title: series?.title ?? "", userErrors: [] },
  );

  // The loader guarantees this; the type does not.
  if (!series) return null;

  const saving = isPending || updateSeries.isPending;

  const fieldError = (name: string) => {
    const error = state.userErrors.find((item) => item.field.includes(name));
    return error ? (
      <p role="alert" className="text-sm text-brand">
        {error.message}
      </p>
    ) : null;
  };

  const handleCancel = async () => {
    await cancelSeries.mutateAsync(series.id);
    await navigate({ to: "/events" });
  };

  return (
    <>
      <AppHeader />
      <main className="mx-auto max-w-2xl p-6">
        <Link
          to="/events"
          className="text-sm text-content-muted hover:text-content"
        >
          ← All events
        </Link>

        <div className="mt-4 flex items-start justify-between gap-4">
          <h1
            className={
              series.isCancelled
                ? "text-2xl font-semibold line-through decoration-2 text-content-muted"
                : "text-2xl font-semibold"
            }
          >
            {series.title}
          </h1>
          {series.isCancelled && (
            <span className="shrink-0 rounded-full bg-brand px-2.5 py-1 text-xs font-medium text-brand-contrast">
              Cancelled
            </span>
          )}
        </div>

        <p className="mt-1 text-sm text-content-muted">
          {describeRepeat(
            parseRepeat(series.rrule),
            new Date(series.startsAtFirst),
            series.until ? new Date(series.until) : null,
          )}
        </p>

        {/*
          Stated plainly, because it is the one thing that makes this page
          different from an occurrence's edit form: what is saved here reaches
          every night that has not been edited on its own.
        */}
        <p className="mt-4 rounded-md border border-dashed border-border-subtle p-3 text-sm text-content-muted">
          This is the whole run. Changes here apply to every occurrence that has
          not been edited on its own — a night someone has already changed keeps
          what they set.
        </p>

        {series.organisers.length > 0 && (
          <p className="mt-3 text-sm text-content-muted">
            Put on by{" "}
            {series.organisers
              .map((involvement) => involvement.organiser.name)
              .join(", ")}
            .
          </p>
        )}

        {!series.isMine && (
          <p className="mt-4 rounded-md bg-surface-muted px-3 py-2 text-sm text-content-muted">
            Someone else created this series, so it is read-only for you.
          </p>
        )}

        {series.isMine && !series.isCancelled && (
          <>
            <form action={submit} className="mt-6 flex flex-col gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="title" className="text-sm">
                  Title
                </label>
                <input
                  id="title"
                  name="title"
                  // Uncontrolled, seeded from the echoed-back value so a
                  // validation failure does not discard what was typed. React 19
                  // resets a `<form action>` on submit, hence defaultValue.
                  defaultValue={state.title || series.title}
                  required
                  maxLength={200}
                  aria-invalid={state.userErrors.some((error) =>
                    error.field.includes("title"),
                  )}
                  className={inputClass}
                />
                {fieldError("title")}
                <p className="text-xs text-content-muted">
                  Renames future occurrences too, except ones edited on their
                  own.
                </p>
              </div>

              <RepeatFields
                rrule={series.rrule}
                untilLocal={toDateInputValue(series.until)}
                /*
                 * The series' first occurrence, in the format the component's
                 * `new Date(...)` expects. This is what the weekly rule's day is
                 * taken from, so it must be the run's start rather than today.
                 */
                startsAtLocal={series.startsAtFirst}
                fieldError={fieldError}
              />

              {/* Errors with no field of their own — an authorization refusal
                  arrives this way, and would otherwise vanish. */}
              {state.userErrors
                .filter((error) => error.field.length === 0)
                .map((error) => (
                  <p
                    key={error.code}
                    role="alert"
                    className="text-sm text-brand"
                  >
                    {error.message}
                  </p>
                ))}

              <button
                type="submit"
                disabled={saving}
                className="w-fit rounded-md bg-brand px-4 py-2 font-medium text-brand-contrast hover:bg-brand-hover disabled:opacity-60"
              >
                {saving ? "Saving…" : "Save series"}
              </button>
            </form>

            {/*
              The attachments — the reason this page exists.

              Separate from the form above and saved immediately, like everywhere
              else they appear: an image is a row with its own id, not a field of
              the series. No `inheritedFrom` and no `revertHint`, because a series
              inherits from nothing — it is the top of the chain.
            */}
            <div className="mt-8 flex flex-col gap-8 border-t border-border-subtle pt-6">
              <ImagesEditor
                owner="series"
                ownerId={series.id}
                images={series.images}
                heading="Images for the whole run"
              />

              <LinksEditor
                owner="series"
                ownerId={series.id}
                links={series.links}
                heading="Links for the whole run"
              />
            </div>

            <p className="mt-3 text-xs text-content-muted">
              Every night in this run shows these, unless it has its own.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-border-subtle pt-6">
              {confirming ? (
                <>
                  <span className="text-sm text-content-muted">
                    Cancel the whole run? Nights already edited on their own are
                    kept.
                  </span>
                  <button
                    type="button"
                    onClick={() => void handleCancel()}
                    disabled={cancelSeries.isPending}
                    className="rounded-md bg-brand px-4 py-2 font-medium text-brand-contrast hover:bg-brand-hover disabled:opacity-60"
                  >
                    {cancelSeries.isPending ? "Cancelling…" : "Yes, cancel it"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setConfirming(false);
                    }}
                    className="rounded-md border border-border-subtle px-4 py-2 hover:bg-surface-muted"
                  >
                    Keep it
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setConfirming(true);
                  }}
                  className="rounded-md border border-border-subtle px-4 py-2 text-brand hover:bg-surface-muted"
                >
                  Cancel series
                </button>
              )}
            </div>
          </>
        )}

        {/* Read-only view for someone else's series: what it shows, without the
            editors. Shown after the notice above rather than instead of it. */}
        {!series.isMine &&
          (series.images.length > 0 || series.links.length > 0) && (
            <div className="mt-6 flex flex-col gap-4 border-t border-border-subtle pt-6">
              {series.images.length > 0 && (
                <div className="grid gap-2 sm:grid-cols-2">
                  {series.images.map((image) => (
                    <img
                      key={image.id}
                      src={image.url}
                      alt={image.altText ?? ""}
                      width={image.width ?? undefined}
                      height={image.height ?? undefined}
                      className="w-full rounded-lg object-cover"
                    />
                  ))}
                </div>
              )}
              {series.links.length > 0 && (
                <ul className="flex flex-col gap-1">
                  {series.links.map((link) => (
                    <li key={link.id}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="break-all text-brand underline"
                      >
                        {link.label ?? link.url}
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
      </main>
    </>
  );
}
